import React, { useState, useEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
import DatapointInput from '/src/Components/DatapointInput/DatapointInput';
import CTAButton from '/src/Components/CTAButton/CTAButton';
import { CircleX } from 'lucide-react';



import './style.css';

const ContentWrapper = ({ children, inline = false, handleOutsideClick = null, deviceType = null }) => {
    if (inline) {
        return children;
    } else {
        const popupRef = useRef(null);

        useEffect(() => {
            const handleClick = (event) => {
                if (popupRef.current && !popupRef.current.contains(event.target)) {
                    handleOutsideClick?.();
                }
            };

            if (handleOutsideClick) {
                document.addEventListener('mousedown', handleClick);
                return () => document.removeEventListener('mousedown', handleClick);
            }
        }, [handleOutsideClick]);

        // Add effect to handle body scroll
        useEffect(() => {
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = 'unset';
            };
        }, []);

        return createPortal(
            <div className="user-story-personalization-form-overlay">
                <div
                    className={`user-story-personalization-form-popup ${deviceType}`}
                    ref={popupRef}
                    onClick={e => e.stopPropagation()}
                >
                    {handleOutsideClick && (
                        <button
                            className="user-story-personalization-form-close-btn"
                            aria-label="Close personalization form"
                            onClick={handleOutsideClick}
                            type="button"
                        >
                            <CircleX />
                        </button>
                    )}
                    {children}
                </div>
            </div>,
            document.body
        );
    }
};

const UserPersonalInfoForm = ({ handleOutsideClick, inline = false, inRiskOverview = false }) => {

    const { portalData } = useContext(AddressSearchContext);
    const { icons, datapoints = [] } = portalData;

    const { deviceType } = useContext(WindowSizeContext);

    const { userPersonalInfo, setUserPersonalInfo, userPersonalInfoAnon, setUserPersonalInfoAnon } = useContext(AreaDataContext);

    const [userPersonalInfoCopy, setUserPersonalInfoCopy] = useState({});
    const [isDataComplete, setIsDataComplete] = useState(false);
    const [buttonHoverStatus, setButtonHoverStatus] = useState(false);

    const relationshipToHomeOptions = [
        {
            text: "I own this home",
            image: "Lady-Child-Project.jpg",
            value: "owner",
            id: 'relationToProperty'
        },
        {
            text: "I'm thinking about buying this home",
            image: "Man-and-Woman-Planning.jpg",
            value: "buyer",
            id: 'relationToProperty'
        },
    ];

    const additionalRelationshipToHomeOptionsBuyer = [
        {
            id: "expectedPurchasePrice",
            headline: "What is the expected purchase price of this home?",
            data_type: "num",
            num_type: "int_dollar",
            datapoint_value: userPersonalInfoCopy.expectedPurchasePrice,
            datapoint_value_updated: userPersonalInfoCopy.expectedPurchasePrice
        }
    ]
    const additionalRelationshipToHomeOptionsOwner = [
        {
            id: "monthlyMortgagePayment",
            headline: "What is your monthly mortgage payment?",
            data_type: "num",
            num_type: "int_dollar",
            datapoint_value: userPersonalInfoCopy.monthlyMortgagePayment,
            datapoint_value_updated: userPersonalInfoCopy.monthlyMortgagePayment,
            disclaimer: "It's ok to estimate! If no mortgage, just put $0."
        }
    ]
    
    
    const financialQuestions = [
        {
            id: "householdIncome",
            headline: "What is your household income?",
            data_type: "num",
            num_type: "int_dollar",
            datapoint_value: userPersonalInfoCopy.householdIncome,
            datapoint_value_updated: userPersonalInfoCopy.householdIncome
        },
        {
            id: "availableFunds",
            headline: "How large of a sudden expense could you handle?",
            data_type: "num",
            num_type: "int_dollar",
            datapoint_value: userPersonalInfoCopy.availableFunds,
            datapoint_value_updated: userPersonalInfoCopy.availableFunds
        }
    ]

    // Make a copy of the userPersonalInfo object on mount
    useEffect(() => {
        const userPersonalInfoCopy = { ...userPersonalInfo };
        setUserPersonalInfoCopy(userPersonalInfoCopy);

    }, [userPersonalInfo]);


    // useEffect to check if the data is complete
    useEffect(() => {
        const isComplete = userPersonalInfo.isComplete;
        setIsDataComplete(isComplete);
    }, [userPersonalInfo]);

    // Handler for updating a financial datapoint
    const handleUserPersonalInfoUpdate = (id, value) => {

        const initialUserPersonalInfo = { ...userPersonalInfo };

        //   Check if the value is a number and if not (and is not undefined) then set to null
        let updateValue = value;
        if (updateValue.length === 0 || updateValue === undefined) {
            updateValue = null;
        }

        // Update that key/value pair in the initialUserPersonalInfo object
        initialUserPersonalInfo[id] = updateValue;

        const baselineMustBeComplete = ["householdIncome", "availableFunds", "relationToProperty"];
        const additionalMustBeCompleteOwner = ["monthlyMortgagePayment"];
        const additionalMustBeCompleteBuyer = ["expectedPurchasePrice"];

        let isDataComplete = false;
        if (baselineMustBeComplete.every(value => initialUserPersonalInfo[value] !== null)) {
            if (initialUserPersonalInfo.relationToProperty === "buyer") {
                if (additionalMustBeCompleteBuyer.every(value => initialUserPersonalInfo[value] !== null)) {
                    isDataComplete = true;
                }
            } else if (initialUserPersonalInfo.relationToProperty === "owner") {
                if (additionalMustBeCompleteOwner.every(value => initialUserPersonalInfo[value] !== null)) {
                    isDataComplete = true;
                }
            }
        }

        // Update the initialUserPersonalInfo object and set isComplete within that object to true if all the data is filled out
        initialUserPersonalInfo.isComplete = isDataComplete;

        setUserPersonalInfo(initialUserPersonalInfo);
    }

    // Handler for clicking the see my score button
    const handleSeeMyScoreClick = () => {
        handleOutsideClick?.();
    }

    // Handler for when the button is being hovered over
    const handleButtonHover = () => {
        setButtonHoverStatus(true);
    }

    // Handler for when the button is not being hovered over
    const handleButtonHoverOut = () => {
        setButtonHoverStatus(false);
    }

    // Dummy onClick handler for the button for when the data is incomplete
    const handleDummyButtonClick = () => {
        return;
    }

    const missingDataText = "Looks like we're still missing some information. Please make sure you select your relationship to the home and fill out each input."

    const popupHeader = "Personalize the Analysis"
    const popupSubheader = "In order to score this home we need to understand a little more about you and your relationship to this home. This simply allows us to take our modeling and <i>personalize the takeaways</i> to your specific situation.  Don't worry, this information is never shared!"

    const youAndThisHomeIcon = "person-and-house-icon-green.png"
    const youAndThisHomeIconUrl = CreateCloudfrontUrl(youAndThisHomeIcon);
    const youAndThisHomeHeader = "Your Relationship to This Home"

    const householdFinancesIcon = "house-wallet-icon.png"
    const householdFinancesIconUrl = CreateCloudfrontUrl(householdFinancesIcon);
    const householdFinancesHeader = "Household Finances"

    // console.log(additionalRelationshipToHomeDatapoints);

    return (
        <ContentWrapper handleOutsideClick={handleOutsideClick} inline={inline} deviceType={deviceType}>
            <div className={`user-story-personalization-form-content ${inline ? 'inline' : 'popup'} ${deviceType}`}>
                {!inline && (
                    <>
                        <div className="user-story-personalization-form-header-container moving-blocks-background">
                            <h2>{popupHeader}</h2>
                        </div>
                        <p className="user-story-personalization-form-subheader text-align-center" dangerouslySetInnerHTML={{ __html: popupSubheader }} />
                    </>
                )}
                <div className={`user-story-personalization-form-content-container ${deviceType}`}>
                    <div className="user-story-personalization-form-selection-container">
                        <div className="user-story-personalization-form-selection-container-header">
                            <div className="user-story-personalization-form-selection-container-header-icon">
                                <img src={youAndThisHomeIconUrl} alt={youAndThisHomeHeader} />
                            </div>
                            <span>{youAndThisHomeHeader}</span>
                        </div>
                        <div className="user-story-personalization-form-selection-container-options">
                            {relationshipToHomeOptions.map((option) => {

                                return (
                                    <div
                                        key={option.value}
                                        className={`user-story-personalization-form-selection-box ${userPersonalInfo.relationToProperty ? userPersonalInfo.relationToProperty === option.value ? 'selected' : 'not-selected' : ''}`}
                                        style={{ backgroundImage: `url(${CreateCloudfrontUrl(option.image)})` }}
                                        onClick={() => handleUserPersonalInfoUpdate(option.id, option.value)}
                                    >
                                        <div className="user-story-personalization-form-selection-box-overlay">
                                            <span className="user-story-personalization-form-selection-box-overlay-text text-align-center">{option.text}</span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        {userPersonalInfoCopy.relationToProperty && (
                            <div className="user-story-personalization-form-selection-container-options">
                                {userPersonalInfoCopy.relationToProperty === "buyer" ? additionalRelationshipToHomeOptionsBuyer.map((datapoint) => {
                                    return (
                                        <DatapointInput
                                            key={datapoint.id}
                                            datapoint={datapoint}
                                            onChange={(value) => handleUserPersonalInfoUpdate(datapoint.id, value)}
                                        />
                                    )
                                }) : additionalRelationshipToHomeOptionsOwner.map((datapoint) => {
                                    return (
                                        <DatapointInput
                                            key={datapoint.id}
                                            datapoint={datapoint}
                                            onChange={(value) => handleUserPersonalInfoUpdate(datapoint.id, value)}
                                        />
                                    )
                                })}
                            </div>
                        )}
                    </div>
                    <div className="user-story-personalization-form-selection-container">
                        <div className="user-story-personalization-form-selection-container-header">
                            <div className="user-story-personalization-form-selection-container-header-icon">
                                <img src={householdFinancesIconUrl} alt={householdFinancesHeader} />
                            </div>
                            <span>{householdFinancesHeader}</span>
                        </div>
                        <div className="user-story-personalization-form-selection-container-options">
                            {financialQuestions.map((question) => {
                                return (
                                    <DatapointInput
                                        key={question.id}
                                        datapoint={question}
                                        onChange={(value) => handleUserPersonalInfoUpdate(question.id, value)}
                                    />
                                )
                            })}
                        </div>
                    </div>
                </div>

                {!inline && (
                    <div
                        className={`user-story-personalization-form-button-container ${isDataComplete ? 'data-complete' : 'data-incomplete'}`}
                        onMouseEnter={handleButtonHover}
                        onMouseLeave={handleButtonHoverOut}
                    >
                        <CTAButton text="See My Score" onClick={isDataComplete ? handleSeeMyScoreClick : handleDummyButtonClick} />
                        {(!isDataComplete && buttonHoverStatus) && (
                            <span className="user-story-personalization-form-missing-data-text">{missingDataText}</span>
                        )}
                    </div>
                )}
            </div>
        </ContentWrapper>
    );
};

export default UserPersonalInfoForm;

